import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { AdminExecuteRefund, AdminFetchRefunds } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  ExecuteRefundPayload,
  ExecuteRefundResult,
  FetchRefundsPayload,
  RefundsPage,
} from 'src/interfaces/refund'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onFetchRefunds(action: AsyncAction<FetchRefundsPayload>) {
  const { status, limit, offset } = action.payload

  try {
    // All three are always sent: `status` is required (there is no "all"), and the
    // `@Transform(parseInt(value || '20'))` default runs only for a present-but-empty key.
    const { payload }: { payload: RefundsPage } = yield call(
      callSecureApi,
      Endpoints.AdminFetchRefunds,
      { method: 'GET', query: { status, limit, offset } },
    )

    yield put(AdminFetchRefunds.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.refund.onFetchRefunds] error' })

    yield put(AdminFetchRefunds.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load refunds'))
    }
  }
}

export function* onExecuteRefund(action: AsyncAction<ExecuteRefundPayload>) {
  const { refundId, body, refetch } = action.payload

  try {
    const { payload }: { payload: ExecuteRefundResult } = yield call(
      callSecureApi,
      Endpoints.AdminExecuteRefund,
      {
        method: 'POST',
        params: [{ field: ':refundId', value: refundId }],
        body,
      },
    )

    yield put(AdminExecuteRefund.success(payload))
    yield put(AdminFetchRefunds.request(refetch))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.refund.onExecuteRefund] error' })

    yield put(AdminExecuteRefund.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Refund was not sent — try again'))
    }
  }
}

export function* refundSaga() {
  yield takeLatest(AdminFetchRefunds.type.REQUEST, onFetchRefunds)
  yield takeEvery(AdminExecuteRefund.type.REQUEST, onExecuteRefund)
}
